import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { Router, RouterModule, RouterOutlet } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatNavList } from '@angular/material/list';
import { MatTabsModule } from '@angular/material/tabs';
import { AuthService } from '../core/services/auth.service';
import { NavItem } from '../core/interfaces/nav-item.interface';
import { MatMenuModule } from '@angular/material/menu';
import { UserService } from '../core/services/user.service';
import { Roles } from '../core/enums/user.enum';
import { MatDialog } from '@angular/material/dialog';
import { ChangePasswordComponent } from '../features/profile/change-password/change-password.component';
import { FooterComponent } from '../shared/components/footer/footer.component';
import { ActivityListComponent } from '../features/activity-list/activity-list.component';
import { CommonModule } from '@angular/common';
import { ActivityService } from '../core/services/activity.service';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
@Component({
  selector: 'app-main-layout',
  imports: [RouterOutlet, RouterModule, MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatDividerModule,
    MatSidenavModule,
    MatNavList,
    MatTabsModule,
    MatMenuModule, FooterComponent, ActivityListComponent, CommonModule, MatSlideToggleModule],
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
})
export class LayoutComponent implements OnInit {
  private auth = inject(AuthService);
  private user = inject(UserService);
  private activityService = inject(ActivityService);
  private cdr = inject(ChangeDetectorRef);
  router = inject(Router);
  private dialog = inject(MatDialog);
  userName: string | null = null;
  userRole: string | null = null;
  menuOpened = false;
  selectedActivity: string | null = null;
  showCookieModal = false;
  isFileUploadEnabled = true;

  navItems: NavItem[] = [
    { label: 'Upload History', icon: 'history', route: '/upload-history' },
    { label: 'File Upload', icon: 'upload_file', route: '/file-upload' },
  ];

  isLoggedIn() {
    return this.auth.isAuthenticated();
  }
  
  logout() {
    this.auth.logout();
  }

  ngOnInit(): void {
    this.userRole = this.user.currentUser?.roleName ?? null;
    this.updateNavItems(this.userRole);
    this.user.roleChanged$.subscribe(role => {
      if (role) {
        this.userRole = this.user.currentUser?.roleName ?? null;
        this.updateNavItems(this.userRole);
        this.cdr.markForCheck();
      }
    });
    const localUser = this.user.getUserFromLocal();
    if (localUser) {
      this.userRole = localUser.role;
      this.updateNavItems(this.userRole);
    }
    this.user.loadUserDetails().subscribe({
      next: () => {
        const user = this.user.currentUser;
        this.userName = user?.firstName ?? user?.name ?? null;
        this.userRole = user?.roleName ?? null;
        this.isFileUploadEnabled = user?.isFileUploadEnabled ?? true;

        if (!user?.isCookieAccepted) {
          this.showCookieModal = true;
        }

        this.updateNavItems(this.userRole);
      },
      error: (err) => {
        console.error('Error fetching user details:', err);
      }
    });
    this.activityService.activityType$.subscribe(type => {
      this.selectedActivity = type;
      this.cdr.markForCheck(); 
    });
  }

  toggleFileUpload(): void {
    this.user.toggleFileUpload().subscribe({
      next: (res) => {
        this.isFileUploadEnabled = res.data?.isFileUploadEnabled ?? true;
        this.updateNavItems(this.userRole);

        // ── If disabled while on file-upload page → redirect away ──
        if (!this.isFileUploadEnabled &&
          this.router.url.includes('/file-upload') &&
          this.userRole !== Roles.Administrator &&
          this.userRole !== Roles.KingsettUser) {
          this.router.navigate(['/upload-history']);
        }

        this.cdr.markForCheck();
      },
      error: (err) => console.error('Failed to toggle file upload', err)
    });
  }

  onProfileMenuClick(sidenav: MatSidenav): void {
    if (sidenav.mode === 'over' || window.innerWidth < 992) {
      sidenav.close();
    }
  }

  openChangePassword(sidenav: MatSidenav): void {
    this.onProfileMenuClick(sidenav);
    const dialogRef = this.dialog.open(ChangePasswordComponent, {
      width: '60%',
      height: '90%',
      data: { field: 'password' },
      disableClose: true,
      panelClass: 'profile-edit-dialog',
    });
    
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        console.log('Password changed successfully.');
      }
    });
  }

  checkActivityType(label: string): boolean {
    if(this.selectedActivity && this.selectedActivity == 'UploadHistory' && label == 'Upload History'){
      return true;
    }else if (this.selectedActivity && this.selectedActivity == 'FileManagement' && label == 'File Upload'){
      return true;
    } 
    else if (this.selectedActivity && this.selectedActivity == 'UserManagement' && label == 'Manage Users'){
      return true;
    } 
    return false;
  }

  updateCookieConsent() {
    return this.user.updateCookieConsent().subscribe({
      next: () => {
        this.showCookieModal = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error updating cookie consent:', err);
      }
    });
  }
  
  canChangePassword(): boolean {
    return this.userRole !== Roles.Administrator && this.userRole !== Roles.KingsettUser;
  }

  // private updateNavItems(role: string | null) {
  //   this.navItems = [
  //     { label: 'Upload History', icon: 'history', route: '/upload-history' },
  //     { label: 'File Upload', icon: 'upload_file', route: '/file-upload' },
  //   ];

  //   const manageUsersExists = this.navItems.some(item => item.route === '/users');

  //   if ((role === Roles.Administrator || role === Roles.KingsettUser) && !manageUsersExists) {
  //     this.navItems.push({
  //       label: 'Manage Users',
  //       icon: 'group',
  //       route: '/users'
  //     });
  //   }
  // }

  private updateNavItems(role: string | null) {
    const isAdminOrKingsett = role === Roles.Administrator || role === Roles.KingsettUser;

    this.navItems = [
      { label: 'Upload History', icon: 'history', route: '/upload-history' },
    ];

    // Admins and KingSett users always see File Upload — toggle doesn't affect them
    // Regular users only see it when enabled
    if (isAdminOrKingsett || this.isFileUploadEnabled) {
      this.navItems.push({
        label: 'File Upload',
        icon: 'upload_file',
        route: '/file-upload'
      });
    }

    if (isAdminOrKingsett) {
      this.navItems.push({
        label: 'Manage Users',
        icon: 'group',
        route: '/users'
      });
    }
  }

}
